import moment from "moment";

import TrainingCourseListCreate from "issara-sdk/apis/TrainingCourseListCreate_apps_HRM";
import TrainingCourseRetrieveUpdateDestroy from "issara-sdk/apis/TrainingCourseRetrieveUpdateDestroy_apps_HRM";
import TrainingCourseSerializerForList from "issara-sdk/types/TrainingCourseSerializerForList_apps_HRM";
import EmployeeListAPI from "issara-sdk/apis/EmployeeListAPI_users";

import getPdfMake from "react-lib/appcon/common/pdfMake";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import FormTrainingBookingReport from "../pdfForm/FormTrainingBookingReport";

import { COURSE_STATUS_OPTIONS, COURSE_TYPE_OPTIONS, getCourseStatusColor } from "../HRMInterface";

import { beToAd, formatDate } from "react-lib/utils/dateUtils";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  PRINT_REPORT: "PRINT_REPORT",
  SAVE: "SAVE",
  SEARCH: "SEARCH",
  SELECT: "SELECT",
  ADD_DIVISION: "ADD_DIVISION",
  TOGGLE_ACTIVE: "TOGGLE_ACTIVE",
} as const;

export const SEQUENCE_NAME = "CourseList" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  CourseListSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    detail: Partial<CourseDetailType> | null;
    filter: FilterType;
    pagination: { activePage: number; items: Partial<CourseDetailType>[]; total: number };
  }> | null;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "django" | "errorMessage" | "searchedItemListWithKey"
>;

type CourseDetailType = Omit<
  TrainingCourseSerializerForList,
  "evaluation" | "links" | "participants"
> & {
  end_time: string;
  evaluation: { inactive?: boolean; link: string; subject: string }[];
  is_approved?: boolean;
  is_editing?: boolean;
  links: { inactive?: boolean; link: string; title: string }[];
  participants: (ParticipantType & { inactive?: boolean })[];
  start_time: string;
  participant?: string;
  division?: number;
  is_active: boolean;
};

type ParticipantType = Required<TrainingCourseSerializerForList>["participants"][number];

type FilterType = Partial<{
  course_name_id: number | null;
  end_date: string;
  start_date: string;
  status: string[];
  training_type: string;
}>;

export type RequiredFieldType = Partial<
  Record<"end_date" | "period" | "pricing" | "start_date" | "title" | "type", string>
>;

export type ActionSaveType = "NO_SEND" | "SEND";

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SAVE]: {
    canceled?: boolean;
    isByPass?: boolean;
    type: ActionSaveType;
    onConfirm: (type: ActionSaveType) => void;
    onSuccess: () => void;
  };
  [ACTIONS.SEARCH]: { activePage?: number };
  [ACTIONS.SELECT]: { id: number };
  [ACTIONS.TOGGLE_ACTIVE]: {};
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  CourseListSequence: null,
};

export const DataInitial: Data = {};

const LIST_LIMIT = 100;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    { CourseListSequence: { ...state.CourseListSequence, sequenceIndex: "Action" } },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.PRINT_REPORT]: HandlePrintReport,
    [ACTIONS.SAVE]: HandleSave,
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.SELECT]: HandleSelect,
    [ACTIONS.ADD_DIVISION]: HandleAddDivision,
    [ACTIONS.TOGGLE_ACTIVE]: HandleToggleActive,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<Actions["SEARCH"]>, Promise<void>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const activePage = params.activePage ?? 1;

  const [result] = await GetTrainingCourseList(controller, { activePage });

  controller.setState({
    CourseListSequence: {
      ...state.CourseListSequence,
      pagination: { activePage, items: result?.items ?? [], total: result?.total ?? 0 },
    },
  });

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};

const HandleSave: Handler<Params<Actions["SAVE"]>> = async (controller, params) => {
  let state = controller.getState();

  const byPassKey = params.isByPass ? "_CHANGE" : "";
  const btnKey = `${BTN_ACTS.SAVE}_${params.type}${byPassKey}`;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const detail = state.CourseListSequence?.detail ?? {};

  const data = {
    ...detail,
    create_user: state.django?.user?.id,
    end_date: `${beToAd(detail.end_date || "")?.format("YYYY-MM-DD")} ${detail.end_time}`,
    start_date: `${beToAd(detail.start_date || "")?.format("YYYY-MM-DD")} ${detail.start_time}`,
    expiry_date: beToAd(detail.expiry_date || "")?.format("YYYY-MM-DD"),
    evaluation: (detail.evaluation ?? []).filter((item) => !item.inactive),
    links: (detail.links ?? []).filter((item) => !item.inactive),
    participants: detail.participants ?? [],
    is_send_email: params.type === "SEND",
    by_pass: params.isByPass,
    participants_detail: undefined,
    status: "OPEN" as const,
  };

  let api;

  if (params.canceled) {
    api = TrainingCourseRetrieveUpdateDestroy.delete;
  } else if (data.id) {
    api = TrainingCourseRetrieveUpdateDestroy.update;
  } else {
    api = TrainingCourseListCreate.create;
  }

  const [result, error] = await api({
    apiToken: controller.apiToken,
    data,
    pk: data.id,
    extra: params.canceled ? { data: { is_send_email: data.is_send_email } } : {},
  });

  if (error) {
    // มีการอนุมัติการเข้าร่วมกิจกรรม อย่างน้อย 1 participant ในคอร์สนั้น ของช่วงวันที่อบรม จะมี pop-up แจ้งเตือน
    if (error.course) {
      controller.setState({ buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null } });

      params.onConfirm(params.type);

      return;
    }

    SetErrorMessage(controller, { ...params, action: btnKey, error });

    return;
  }

  await HandleSearch(controller, { ...params, action: ACTIONS.SEARCH });

  state = controller.getState();

  function formatDateBE(m: moment.Moment) {
    if (!m.isValid()) return "";
    const day = m.date().toString().padStart(2, "0");
    const month = (m.month() + 1).toString().padStart(2, "0");
    const yearBE = m.year() + 543;
    return `${day}/${month}/${yearBE}`;
  }

  const normalizeDetail = (r: any) => {
    const start = r.start_date ? moment(r.start_date) : null;
    const end = r.end_date ? moment(r.end_date) : null;
    const expiry = r.expiry_date ? moment(r.expiry_date) : null;

    return {
      ...r,
      start_date: start ? formatDateBE(start) : "",
      start_time: start ? start.format("HH:mm") : "",
      end_date: end ? formatDateBE(end) : "",
      end_time: end ? end.format("HH:mm") : "",
      expiry_date: expiry ? formatDateBE(expiry) : "",
      participants: r.participants_detail ?? r.participants ?? [],
    };
  };

  controller.setState({
    CourseListSequence: {
      ...state.CourseListSequence,
      detail: params.canceled ? null : normalizeDetail(result),
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
  });

  params.onSuccess();
};

const HandleSelect: Handler<Params<Actions["SELECT"]>> = async (controller, params) => {
  const [result] = await TrainingCourseRetrieveUpdateDestroy.retrieve({
    apiToken: controller.apiToken,
    pk: params.id,
  });

  const state = controller.getState();

  controller.setState({
    CourseListSequence: {
      ...state.CourseListSequence,
      detail: {
        ...result,
        end_date: formatDate(moment(result.end_date)),
        end_time: moment(result.end_date).format("HH:mm"),
        expiry_date: formatDate(moment(result.expiry_date)),
        participants: result.participants_detail,
        start_date: formatDate(moment(result.start_date)),
        start_time: moment(result.start_date).format("HH:mm"),
      },
    },
  });
};

const HandlePrintReport: Handler = async (controller) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PRINT_REPORT]: BLC_STATUS.LOADING },
  });

  const [result] = await GetTrainingCourseList(controller, { limit: 999999, offset: 0 });

  const filter = state.CourseListSequence?.filter ?? {};

  const items = result?.items ?? [];
  const formattedItems = items.map((item) => {
    const typeName = COURSE_TYPE_OPTIONS.find((option) => option.value === item.training_type);
    const statusName = COURSE_STATUS_OPTIONS.find((option) => option.value === item.status);

    return {
      ...item,
      id: String(item.id),
      created: item.created || "",
      status: statusName?.text || "",
      status_color: getCourseStatusColor(item.status),
      training_type: typeName?.text || "",
    };
  });

  const docDef = await FormTrainingBookingReport({
    fields: formattedItems,
    params: {
      date_range: `${filter.start_date || ""} - ${filter.end_date || ""}`,
      printed_date: moment().format("DD/MM/YYYY, HH:mm"),
    },
  });

  const pdfMake = await getPdfMake(true);
  const pdf = pdfMake.createPdf(docDef);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PRINT_REPORT]: BLC_STATUS.SUCCESS },
  });

  pdf.open();
};

const HandleAddDivision: Handler<Params<Actions["ADD_DIVISION"]>, Promise<void>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PRINT_REPORT]: BLC_STATUS.LOADING },
  });

  const [employeeListRes, employeeListErr, employeeListNet] = await EmployeeListAPI.list({
    apiToken: controller.apiToken,
    params: {
      affiliate: state.CourseListSequence?.detail?.division,
    },
  });

  if (employeeListRes?.items?.length) {
    const newParticipants = employeeListRes.items.map(
      (item: { id: string; full_name: string }) => ({
        ...item,
        id: null,
        employee: item?.id,
        employee_name: item?.full_name,
      })
    );

    const existing = state.CourseListSequence?.detail?.participants ?? [];
    const merged = [...existing, ...newParticipants];

    const uniqueParticipants = merged.filter(
      (participant, index, self) =>
        index === self.findIndex((p) => p.employee === participant.employee)
    );

    controller.setState({
      CourseListSequence: {
        ...state.CourseListSequence,
        detail: {
          ...state.CourseListSequence?.detail,
          participants: uniqueParticipants,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT_REPORT]: BLC_STATUS.SUCCESS,
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT_REPORT]: BLC_STATUS.ERROR,
      },
    });
  }
}

const HandleToggleActive: Handler<Params<Actions["TOGGLE_ACTIVE"]>> = async (
  controller,
  params
) => {
  let state = controller.getState();
  const detail = state.CourseListSequence?.detail;

  if (!detail?.id) return;

  const btnKey = BTN_ACTS.TOGGLE_ACTIVE;
  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const api = TrainingCourseRetrieveUpdateDestroy.update;

  const [result, error] = await api({
    apiToken: controller.apiToken,
    pk: detail.id,
    data: {
      ...detail,
      end_date: `${beToAd(detail.end_date || "")?.format("YYYY-MM-DD")} ${detail.end_time}`,
      start_date: `${beToAd(detail.start_date || "")?.format("YYYY-MM-DD")} ${detail.start_time}`,
      expiry_date: beToAd(detail.expiry_date || "")?.format("YYYY-MM-DD"),
      is_active: !detail.is_active,
    } as any,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, action: "TOGGLE_ACTIVE", error });
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
    });
    return;
  }

  await HandleSearch(controller, { ...params, action: ACTIONS.SEARCH });

  const active_state = controller.getState();

  controller.setState({
    CourseListSequence: {
      ...active_state.CourseListSequence,
      detail: {
        ...detail,
        is_active: result.is_active,
      },
    },
    buttonLoadCheck: { ...active_state.buttonLoadCheck, [btnKey]: BLC_STATUS.SUCCESS },
  });
};

/** ====================================================  */
/**                         APIS                          */
/** ====================================================  */
const GetTrainingCourseList: Handler<
  { activePage?: number; limit?: number; offset?: number },
  Promise<[{ items: CourseDetailType[]; total: number } | null, unknown]>
> = async (controller, params) => {
  const state = controller.getState();

  const filter = state.CourseListSequence?.filter;
  const activePage = params.activePage ?? 1;
  const offset = (activePage - 1) * LIST_LIMIT;

  const response = await TrainingCourseListCreate.list({
    apiToken: controller.apiToken,
    params: { limit: params.limit ?? LIST_LIMIT, offset: params.offset ?? offset, ...filter },
  });

  return response as Promise<[{ items: CourseDetailType[]; total: number } | null, unknown]>;
};
